import React, { useEffect, useState } from "react";
import Input from "../../../form/Input";
import Select from "../../../form/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import useMutation from "../../../../hooks/useMutation";
import { transactionAddEdit } from "../../../../services/transactionService";
import DatePicker from "../../../form/DatePicker";
import FileUploadSingle from "../../../form/FileUploadSingle";
import Textarea from "../../../form/Textarea";
import { removeDuplicateFromArray } from "../../../../helpers";
import { useDebounce } from "../../../../hooks/useDebounce";
import useFetch from "../../../../hooks/useFetch";
import { scholarshipList } from "../../../../services/scholarshipService";
import { paymentMethodList } from "../../../../services/PaymentService";

const validation = {
  firstName: { required: true, message: "Recipient first is required" },
  lastName: { required: true, message: "Recipient last is required" },
  email: [
    {
      type: "email",
      required: true,
      message: "Enter valid Email",
    },
  ],
  scholarshipId: { required: true, message: "Scholarship Name is required" },
  amount: { required: true, message: "Amount is required" },
  paymentMethod: { required: true, message: "Payment Method is required" },
  paymentStatus: { required: true, message: "Payment Status is required" },
};

const ManageFinance = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [scholarshipFilters, setScholarshipFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [scholarshipKeyword, setScholarshipkeyword] = useState("");
  const scholarshipSearch = useDebounce(scholarshipKeyword, 300);

  const { data: paymentData } = useFetch({
    dependencies: ["paymentMethod"],
    fetchFunction: async () => {
      const res = await paymentMethodList({
        limit: 10000,
      });
      return res?.docs;
    },
  });

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({ validationRules: validation });

  useEffect(() => {
    if (data?._id) {
      setFieldsValue({ ...data, pdf: data?.pdf?.url });
    }
  }, [data]);

  const {
    data: scholarships,
    error: scholarshipError,
    isLoading: scholarshipLoading,
    updateData: scholarshipUpdate,
    isFetching: scholarshipFetching,
    refetch: scholarshipRefetch,
  } = useFetch({
    dependencies: ["scholarship", scholarshipFilters, scholarshipSearch],
    fetchFunction: async () => {
      const res = await scholarshipList({
        ...scholarshipFilters,
        ...(scholarshipSearch && { keyword: scholarshipSearch }),
      });
      if (res?.docs?.status === 200) {
        return {
          ...res?.docs,
          data: removeDuplicateFromArray([
            ...(scholarships?.data || []),
            ...res?.docs?.data,
          ]),
        };
      } else {
        if (scholarships?.data?.length > 0) {
          return scholarships;
        } else {
          return res;
        }
      }
    },
  });

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      return await transactionAddEdit({
        ...val,
        categoryType: "fiance",
        ...(data?._id && { id: data?._id }),
      });
    },
    onSuccess: (res) => {
      getResponse(res);
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function loadData(e, obj, setData = () => {}) {
    if (
      Math.ceil(e.target.scrollTop) + 20 >=
        e.target.scrollHeight - e.target.clientHeight &&
      obj?.metadata?.hasNextPage
    ) {
      setData((pre) => ({
        ...pre,
        page: obj?.metadata?.nextPage,
      }));
    }
  }

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-12 gap-x-4 gap-y-4">
        <div className="relative col-span-4">
          <Input
            label="Recipient First Name"
            inputPlaceholder={"Enter Recipient First Name"}
            inputName="firstName"
            value={values?.firstName || ""}
            onChange={handleChange}
            {...formValidate(errors, "firstName")}
          />
        </div>
        <div className="relative col-span-4">
          <Input
            label="Recipient Last Name"
            inputPlaceholder={"Enter Recipient Last Name"}
            inputName="lastName"
            value={values?.lastName || ""}
            onChange={handleChange}
            {...formValidate(errors, "lastName")}
          />
        </div>
        <div className="relative col-span-4">
          <Input
            label="Recipient Email"
            inputPlaceholder={"Enter Recipient Email"}
            inputName="email"
            value={values?.email || ""}
            onChange={handleChange}
            {...formValidate(errors, "email")}
          />
        </div>
        <div className="relative col-span-6">
          <Select
            label={"Scholarship Name"}
            placeholder="Select Scholarship Name"
            selectedValue={values?.scholarshipId}
            dropdownData={removeDuplicateFromArray(
              [
                {
                  name: data?.scholarshipDetails?.title,
                  value: data?.scholarshipDetails?._id,
                },
                ...(scholarships?.data || [])?.map((s) => ({
                  name: s?.title,
                  value: s?._id,
                })),
              ],
              "value"
            )}
            setSelectedValue={(val) => {
              handleChange({
                target: { name: "scholarshipId", value: val },
              });
            }}
            onScroll={(e) => {
              loadData(e, scholarships, setScholarshipFilters);
            }}
            {...formValidate(errors, "scholarshipId")}
          />
        </div>
        <div className="relative col-span-3">
          <Input
            label="Amount"
            inputPlaceholder={"Enter Amount"}
            inputName="amount"
            inputType={"number"}
            value={values?.amount || ""}
            onChange={handleChange}
            {...formValidate(errors, "amount")}
          />
        </div>
        <div className="relative col-span-3">
          <DatePicker
            label={"Disbursement Date"}
            value={{
              startDate: values?.date,
              endDate: values?.date,
            }}
            onChange={(e) => {
              handleChange({
                target: { value: e?.startDate, name: "date" },
              });
            }}
            {...formValidate(errors, "date")}
          />
        </div>
        <div className="relative col-span-6">
          <Select
            label="Payment Method"
            placeholder="Select Payment Method"
            selectedValue={values?.paymentMethod}
            const
            dropdownData={
              paymentData?.data?.map((item) => ({
                name: item?.name,
                value: item?.slug,
              })) || []
            }
            setSelectedValue={(val) => {
              handleChange({
                target: { name: "paymentMethod", value: val },
              });
            }}
            {...formValidate(errors, "paymentMethod")}
          />
        </div>
        <div className="relative col-span-6">
          <Select
            label={"Status"}
            placeholder="Select Status"
            selectedValue={values?.paymentStatus}
            dropdownData={[
              { name: "Success", value: "completed" },
              { name: "Pending", value: "pending" },
              { name: "Cancelled", value: "cancelled" },
            ]}
            setSelectedValue={(val) => {
              handleChange({
                target: { name: "paymentStatus", value: val },
              });
            }}
            {...formValidate(errors, "paymentStatus")}
          />
        </div>
        <div className="relative col-span-12">
          <Textarea
            label={"Note"}
            inputPlaceholder={"Short Note"}
            inputName="note"
            inputValue={values?.note || ""}
            onChange={handleChange}
            {...formValidate(errors, "note")}
          />
        </div>
      </div>
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={"Save"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageFinance;
